/**
 * The pure parts of asking for a review (c133): what's wrong with a typed email before it's sent, which checked
 * rows go in a batch, and handing the tracked review link to the phone. The server judges every row again; this
 * only saves a round trip and keeps a batch inside its size.
 */
import { Email, reviews as R } from '@ff/contracts';
import { classifyAddress } from '@ff/messaging/address';
import type { ParsedAskRow } from '@ff/messaging/parse';
import type { z } from 'zod';

export type AskEntry = R.AskEntry;
export type AskVerdict = R.AskVerdict;
export type RowVerdict = z.infer<typeof R.AskRowVerdict>;

/**
 * Why a typed email can't be asked, or null when it can. Reserved test domains pass here: the server decides
 * those, and its own words show if it refuses.
 */
export function emailProblem(raw: string): 'bad_email' | 'throwaway' | 'no_reply_address' | null {
  const typed = raw.trim();
  const v = classifyAddress(typed, { allowReserved: true });
  if (v.verdict === 'bad_email' || !Email.safeParse(typed).success) return 'bad_email';
  return v.verdict === 'ok' ? null : v.verdict;
}

/** The most rows one send may carry: a paste is smaller than an import of today's jobs. */
export function batchMax(entry: AskEntry): number {
  return entry === 'paste' ? R.REVIEW_ASK_LIMITS.pasteMax : R.REVIEW_ASK_LIMITS.importMax;
}

/** The rows the check passed, in order, kept inside one batch. Only these are ever sent. */
export function rowsToSend(rows: readonly ParsedAskRow[], verdicts: readonly RowVerdict[], entry: AskEntry): ParsedAskRow[] {
  const ok = new Set(verdicts.filter((v) => v.verdict === 'ok').map((v) => v.row));
  return rows.filter((_, i) => ok.has(i)).slice(0, batchMax(entry));
}

/** A row as the API takes it: trimmed, and never longer than the contract allows. */
export function toAskRow(r: ParsedAskRow): z.input<typeof R.AskRow> {
  const first = r.firstName?.trim().slice(0, 60);
  const date = r.jobDate?.trim().slice(0, 40);
  return { email: r.email.trim().slice(0, 254), ...(first ? { firstName: first } : {}), ...(date ? { jobDate: date } : {}) };
}

export type ShareOutcome = 'shared' | 'copied' | 'cancelled' | 'failed';

/**
 * Hand words to the phone's own share sheet so the owner sends them however they like. Where there's no share
 * sheet (most desktops) the words go to the clipboard instead. Closing the sheet isn't an error.
 */
export async function shareWords(title: string, text: string): Promise<ShareOutcome> {
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title, text });
      return 'shared';
    } catch (e) {
      if ((e as Error | null)?.name === 'AbortError') return 'cancelled';
      // Blocked or unsupported for this content: fall through to the clipboard.
    }
  }
  try {
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch {
    return 'failed';
  }
}
